import { Component, Inject } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { AddeableComponent } from '../../../../shared/components/interfaces/addeable.component';
import { EditableComponent } from '../../../../shared/components/interfaces/editable.component';
import { UIActiveDialogService } from '../../../../shared/services/ui-dialog.service';
import { AuthService } from '../../../security/service/auth.service';
import { MakeUIMessages } from '../../model/make-ui.messages';
import { MakeModel } from '../../model/make.model';
import { MakeService } from '../../service/make.service';

@Component({
  selector: 'make-add-dialog',
  templateUrl:
    'console/app-js/modules/make/components/make-add-dialog/make-add-dialog.component.html'
})
export class MakeAddDialogComponent extends APIOperableComponent
  implements AddeableComponent, EditableComponent {
  protected makeModel: MakeModel = new MakeModel();
  private originalModel: MakeModel;
  private makeId: number;
  private allMakes: Array<MakeModel> = [];
  private currentMakeNames: Array<string> = [];

  constructor(
    @Inject('makeModel') make: MakeModel,
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private makeService: MakeService,
    private activeDialogService: UIActiveDialogService
  ) {
    super(progressService, toastr, authService);
    this.makeId = make ? make.id : null;
    this.onLoad();
  }

  /**
   * Loads all makes and extract it's names to validate.
   */
  private loadAllMakes(): void {
    this.loadingIndicator = true;
    this.makeService.getMakes(true).subscribe(
      result => {
        this.allMakes = result ? result : [];
        this.currentMakeNames = this.allMakes.map(item => item.name);
        this.loadingIndicator = false;
      },
      err => this.onAPIError(MakeUIMessages.LOAD_MAKES_LIST_ERROR)
    );
  }

  onSave(): void {
    this.startProgress();
    if (this.originalModel) {
      const makeModel = {
        id: this.makeModel.id,
        name: this.makeModel.name,
        isActive: this.makeModel.isActive,
        requiresAudit: this.makeModel.requiresAudit
      };
      // Update
      this.makeService.updateMake(makeModel).subscribe(
        response => {
          if (response.results && response.results.id) {
            this.onAPISuccess(MakeUIMessages.EDIT_MAKE_SUCCESS);
            this.activeDialogService.close(true);
          }
        },
        error => {
          console.log(error);
          this.onAPIError(MakeUIMessages.EDIT_MAKE_ERROR);
        }
      );
    } else {
      // Create
      this.makeService.createMake(this.makeModel).subscribe(
        response => {
          if (response.results && response.results.id) {
            this.onAPISuccess(MakeUIMessages.ADD_MAKE_SUCCESS);
            this.activeDialogService.close(true);
          }
        },
        error => {
          console.log(error);
          this.onAPIError(MakeUIMessages.ADD_MAKE_ERROR);
        }
      );
    }
  }

  isDirty(): boolean {
    if (this.originalModel) {
      return (
        JSON.stringify(this.originalModel) !== JSON.stringify(this.makeModel)
      );
    } else {
      // Create always dirty
      return true;
    }
  }

  onCancel(): void {
    this.activeDialogService.dismiss();
  }

  /**
   * Determines if the current name input by user is already in use.
   * @returns {boolean}
   */
  protected isNameInUse(): boolean {
    const match =
      this.makeModel.name &&
      this.currentMakeNames.find(
        item =>
          item.toLocaleLowerCase() ===
          this.makeModel.name.trim().toLocaleLowerCase()
      );
    if (match && this.originalModel) {
      return (
        match.toLocaleLowerCase() !==
        this.originalModel.name.toLocaleLowerCase()
      );
    }
    return match && match.length > 0;
  }

  /**
   * Load Make data if it's an update.
   */
  protected onLoad(): void {
    this.loadAllMakes();
    if (this.makeId) {
      this.makeService.getMake(this.makeId).subscribe(
        result => {
          this.originalModel = result;
          this.makeModel = { ...this.originalModel };
        },
        error => {
          console.error(error);
          this.onAPIError(MakeUIMessages.LOAD_MAKE_ERROR);
        }
      );
    } else {
      this.originalModel = null;
    }
  }
}
